import { useNavigate, useLocation } from "react-router-dom";
import { Search, Smartphone, Bell, ChevronDown } from "lucide-react";
import UserAvatar from "./UserAvatar";
import SidebarDossierPicker from "./SidebarDossierPicker";
import { useTransactionsHeaderStore } from "../../store/transactionsHeaderStore";
import type { User } from "@supabase/supabase-js";
import { formatMontantFCFA } from "@/lib/formatMontant";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/utils";

export interface TopBarProps {
  currentPage: string;
  isGrandeEntrepriseComptaEngagement: boolean;
  user: User | null;
  clientDossiers: { id: string; nom: string }[];
  cabinetDossiers: { id: string; nom: string }[];
  notifications?: number;
  onLogout: () => void;
}

export default function TopBar({
  currentPage,
  isGrandeEntrepriseComptaEngagement,
  user,
  clientDossiers,
  cabinetDossiers,
  notifications = 3,
  onLogout,
}: TopBarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { balance, selectedCompteId, setSelectedCompteId, comptes } = useTransactionsHeaderStore();

  const dateLong = new Date().toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const isTransactions =
    location.pathname === "/transactions" || location.pathname.startsWith("/transactions/");

  return (
    <header className="sticky top-0 z-40 flex h-[52px] shrink-0 items-center gap-3 border-b border-[#e4e8ef] bg-white px-6">
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-slate-400">Woahda</span>
            <span className="mx-1 text-[#e4e8ef]" aria-hidden>
              /
            </span>
            <h1 className="truncate text-sm font-semibold text-slate-900">{currentPage}</h1>
          </div>
          <div className="mt-0.5 text-xs text-slate-400">{dateLong}</div>
        </div>

        {isTransactions && (
          <>
            {(() => {
              const fmtBalance = (n: number) => (n >= 0 ? "+ " : "") + formatMontantFCFA(n);
              const dateShort = new Date().toLocaleDateString("fr-FR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              });
              const isTva = location.pathname === "/transactions/tva";
              return (
                <div className="flex flex-wrap items-center gap-2">
                  <div className="relative flex min-w-[180px] items-center gap-2 rounded-md border border-[#e4e8ef] bg-white px-3.5 py-2">
                    <span
                      className={cn(
                        "shrink-0 font-mono text-sm font-extrabold",
                        balance >= 0 ? "text-emerald-600" : "text-red-500"
                      )}
                    >
                      {fmtBalance(balance)}
                    </span>
                    <select
                      value={selectedCompteId}
                      onChange={(e) =>
                        setSelectedCompteId(e.target.value as import("../../store/transactionsHeaderStore").CompteId)
                      }
                      className="min-w-0 flex-1 cursor-pointer border-0 bg-transparent pr-6 text-xs font-semibold text-slate-700 outline-none"
                    >
                      {comptes.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <span className="shrink-0 text-[10px] text-slate-400">{dateShort}</span>
                    <ChevronDown
                      className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                      strokeWidth={2}
                    />
                  </div>
                  <div className="flex gap-0.5 rounded-md border border-[#d8deea] bg-night-soft p-1">
                    <Button
                      type="button"
                      variant={!isTva ? "primary" : "ghost"}
                      size="sm"
                      onClick={() => navigate("/transactions")}
                      className={cn(
                        !isTva && "shadow-sm",
                        isTva && "!border-transparent !bg-transparent font-semibold text-slate-500 hover:!bg-slate-100 hover:!text-slate-800"
                      )}
                    >
                      Opérations
                    </Button>
                    <Button
                      type="button"
                      variant={isTva ? "primary" : "ghost"}
                      size="sm"
                      onClick={() => navigate("/transactions/tva")}
                      className={cn(
                        isTva && "shadow-sm",
                        !isTva && "!border-transparent !bg-transparent font-semibold text-slate-500 hover:!bg-slate-100 hover:!text-slate-800"
                      )}
                    >
                      TVA
                    </Button>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>

      {!isGrandeEntrepriseComptaEngagement && (
        <div className="relative mx-2 hidden max-w-[260px] flex-1 md:block">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            strokeWidth={1.8}
          />
          <input
            className="search-input w-full rounded-md border border-[#e4e8ef] bg-white py-2 pl-9 pr-3 text-sm text-slate-800 outline-none transition-colors placeholder:text-slate-400 focus:border-rose-brand focus:ring-2 focus:ring-[#b03a6e]/20"
            placeholder="Rechercher une transaction, un compte..."
          />
        </div>
      )}

      <div className="ml-auto flex shrink-0 items-center gap-2">
        {isGrandeEntrepriseComptaEngagement ? (
          <UserAvatar user={user} size="md" showBadge variant="compact" onLogout={onLogout} />
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={<Smartphone size={18} strokeWidth={1.8} />}
              className="font-semibold text-slate-700"
            >
              Sync Mobile Money
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              icon={<Bell size={18} strokeWidth={1.8} />}
              className="relative"
            >
              {notifications > 0 ? <div className="notif-badge">{notifications}</div> : null}
            </Button>

            <SidebarDossierPicker clientDossiers={clientDossiers} cabinetDossiers={cabinetDossiers} />

            <UserAvatar user={user} size="md" showBadge variant="compact" onLogout={onLogout} />
          </>
        )}
      </div>
    </header>
  );
}
